import React from "react";
import { Statuses } from "@core";
import { Icon } from "@nubeteck/icons";
import { useGlobalQuery } from "@hooks";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { ArrayUtils, EventUtils } from "@nubeteck/utils";
import { confirmDialog } from "primereact/confirmdialog";
import { EmptyMessage, ConfirmDialog, FiltersDrawer } from "@components";
import {
  CardTable,
  StatusTag,
  PaginatorState,
  IDataTableColumnsProps,
} from "@nubeteck/prime";

import { IMonthlyClosing } from "../../interfaces";
import { useAccountingQuery, useAccountingMutation } from "../../hooks";

const AREA_FILTER_DEFAULT = "fcCierreMensualBuscar";

const MonthlyClosingPage = () => {
  const { data: filters, isPending: isFiltersPending } = useGlobalQuery(
    "Filters",
    "getAllByArea",
    AREA_FILTER_DEFAULT,
  );

  const months = useAccountingQuery("MonthlyClosing", "getAllMonths");
  const changeState = useAccountingMutation("MonthlyClosing", "changeState");
  const createCierreMensual = useAccountingMutation("MonthlyClosing", "create");
  const yearFiscales = useAccountingQuery(
    "MonthlyClosing",
    "getAllYearFiscales",
  );

  const [filterSettingsVisible, setFilterSettingsVisible] =
    React.useState(false);
  const [filterSelected, setFilterSelected] = React.useState<
    number | undefined
  >();
  const [paginator, setPaginator] = React.useState<PaginatorState>({
    page: 0,
    first: 0,
    rows: 10,
  });

  const [selectedYear, setSelectedYear] = React.useState<number>();
  const [selectedMonth, setSelectedMonth] = React.useState<number>();

  const monthlyClosings = useAccountingQuery(
    "MonthlyClosing",
    "getAllByFilter",
    {
      page: paginator.page,
      pageSize: paginator.rows,
      filterId: filterSelected ?? 0,
    },
    { enabled: !!filterSelected },
  );

  React.useEffect(() => {
    const defaultFilterId = filters?.find(
      (filter) => filter.EsPorDefecto,
    )?.FiltroId;
    setFilterSelected(defaultFilterId);
  }, [filters]);

  const renderEmptyMessage = React.useCallback(() => {
    return <EmptyMessage message="No hay cierres mensuales agregados" />;
  }, []);

  const handleOpenMonth = React.useCallback(
    (id: number) => {
      confirmDialog({
        header: "Abrir mes",
        acceptLabel: "Abrir mes",
        acceptClassName: "p-button-sm p-button-success",
        message: "¿Está seguro de que desea abrir este mes?",
        accept: async () => {
          await changeState.mutateAsync({
            Id: id,
            EstadoId: Statuses.OPEN_CODE,
          });
        },
      });
    },
    [changeState],
  );

  const handleCloseMonth = React.useCallback(
    (id: number) => {
      confirmDialog({
        header: "Cerrar mes",
        acceptLabel: "Cerrar mes",
        // acceptClassName: "p-button-sm p-button-success",
        message: "¿Está seguro de que desea cerrar este mes?",
        accept: async () => {
          await changeState.mutateAsync({
            Id: id,
            EstadoId: Statuses.CLOSE_CODE,
          });
        },
      });
    },
    [changeState],
  );

  const handleCloseNewMonth = React.useCallback(() => {
    confirmDialog({
      header: "Abrir nuevo mes",
      acceptLabel: "Abrir nuevo mes",
      acceptClassName: "p-button-sm p-button-success",
      message: "¿Está seguro de que desea abrir este nuevo mes?",
      accept: async () => {
        await createCierreMensual.mutateAsync({
          EstadoId: 1,
          CierreMensualId: 0,
          MesId: selectedMonth ?? 0,
          SucursalAnioFiscalId: selectedYear ?? 0,
        });
      },
    });
  }, [createCierreMensual, selectedMonth, selectedYear]);

  const columns: IDataTableColumnsProps<IMonthlyClosing>[] = [
    {
      header: "Código",
      field: "CierreMensualId",
    },
    {
      header: "Año",
      sortable: true,
      field: "SucursalAnioFiscalAnio",
    },
    {
      header: "Mes",
      field: "MesNombre",
    },
    {
      header: "Estado",
      field: "EstadoNombre",
      body: (data) => <StatusTag status={data.EstadoNombre ?? "Activo"} />,
    },
  ];

  const monthsOptions = React.useMemo(() => {
    if (!months.data?.length) return [];
    return ArrayUtils.selectLabelValue(months.data, "Mes", "MesId");
  }, [months.data]);

  const yearFiscalesOptions = React.useMemo(() => {
    if (!yearFiscales.data?.length) return [];
    return ArrayUtils.selectLabelValue(
      yearFiscales.data,
      "AnioFiscal",
      "SucursalAnioFiscalId",
    );
  }, [yearFiscales.data]);

  const filtersOptions = React.useMemo(() => {
    if (!filters?.length) return [];
    return ArrayUtils.selectLabelValue(filters ?? [], "Nombre", "FiltroId");
  }, [filters]);

  const value = React.useMemo(
    () =>
      monthlyClosings.data?.Data?.filter((monthlyClosing) => {
        const matchesYear = selectedYear
          ? monthlyClosing.SucursalAnioFiscalId === selectedYear
          : true;
        const matchesMonth = selectedMonth
          ? monthlyClosing.MesId === selectedMonth
          : true;
        return matchesYear && matchesMonth;
      }) ?? [],
    [monthlyClosings.data, selectedMonth, selectedYear],
  );

  return (
    <>
      <CardTable
        type="data"
        value={value}
        hideGlobalSearch
        columns={columns}
        rows={paginator.rows}
        enableFilterPagination
        first={paginator.first}
        title="Cierres mensuales"
        valueFilter={filterSelected}
        filterOptions={filtersOptions}
        emptyMessage={renderEmptyMessage()}
        loading={monthlyClosings.isPending}
        isLoadingSearchText={isFiltersPending}
        onRefresh={() => monthlyClosings.refetch()}
        totalRecords={monthlyClosings.data?.TotalRecords}
        onPaginationChange={(event) => setPaginator(event)}
        onSelectFilterOption={(value) => setFilterSelected(value)}
        onClickFilterSettings={EventUtils.callEvent(
          setFilterSettingsVisible,
          true,
        )}
        tableActions={(data) => [
          {
            label: "Abrir mes",
            icon: "calendar-check",
            iconClassName: "text-primary-500",
            disabled: data.EstadoId === Statuses.OPEN_CODE,
            onClick: EventUtils.callEvent(
              handleOpenMonth,
              data.CierreMensualId,
            ),
          },
          {
            icon: "calendar-x",
            label: "Cerrar mes",
            iconClassName: "text-red-500",
            disabled: data.EstadoId === Statuses.CLOSE_CODE,
            onClick: EventUtils.callEvent(
              handleCloseMonth,
              data.CierreMensualId,
            ),
          },
        ]}
        headActions={[
          <Dropdown
            key="Months"
            value={selectedMonth}
            options={monthsOptions}
            placeholder="Selecciona un mes"
            className="p-inputtext-sm w-14rem"
            onChange={(e) => setSelectedMonth(e.value)}
          />,
          <Dropdown
            key="Years"
            value={selectedYear}
            options={yearFiscalesOptions}
            className="p-inputtext-sm w-14rem"
            placeholder="Selecciona un año fiscal"
            onChange={(e) => setSelectedYear(e.value)}
          />,
          <Button
            size="small"
            key="ButtonOpenMonth"
            label="Abrir nuevo mes"
            disabled={!!value.length}
            onClick={handleCloseNewMonth}
            icon={<Icon size={18} className="mr-2" name="calendar-plus" />}
          />,
        ]}
      />
      <ConfirmDialog />
      <FiltersDrawer
        area={AREA_FILTER_DEFAULT}
        visible={filterSettingsVisible}
        onHide={EventUtils.callEvent(setFilterSettingsVisible, false)}
      />
    </>
  );
};

export default MonthlyClosingPage;
